/**
 * Plan-credit grants editor (v2 — DNKADM-0108; PLAN-ONLY).
 *
 * A membership plan's per-bucket credit grants now split by **recurrence**
 * (DNKAPI-0205/0206): a grant is either **recurring** (granted every billing cycle —
 * purchase + each renewal) or an **add_on** (granted once on a qualifying first
 * purchase, with its own re-eligibility window + expiry). This dedicated editor lays
 * the two out as **two sections** — "Recurring credits" and "Add-On credits (first
 * purchase)" — the Add-On rows carrying the two extra controls.
 *
 * It mirrors the proven mechanics of the shared {@link PlanCreditsEditor} (the
 * `useCreditBucketPickList` + `PickListLoader`, the `parseAmount` helper, the add-row
 * `Select` + amount + Add button, the `busy`/`reloadKey` load loop, the load-error
 * Retry) but is a SEPARATE component: Add-On is plan-only, so the shared editor stays
 * simple for Packages (offerings have no recurrence).
 *
 * **One grant per bucket across BOTH sections** — the addable pool excludes any bucket
 * already granted in either section, so a bucket is recurring OR add-on, never both.
 *
 * The three get/set/delete callbacks are the seam: in edit-mode `PlanDialog` wires them
 * to the live `membership-plans/{id}/credits` wrappers; in add-mode it supplies staging
 * callbacks (the grant is attached after the plan is created). Callbacks MUST be stable
 * references (module functions or `useCallback`) — they are effect deps.
 *
 * **Reducing credits for current members (DNKADM-0407 amended U3).** Grants save as soon as they
 * change, so the one write that cuts what current members get — removing a **recurring** grant —
 * is confirmed at the write: when `activeMemberships > 0`, Remove opens "Reduce credits for N
 * current members?" and nothing is sent until staff confirm (Cancel sends nothing and changes
 * nothing). Lowering an amount is remove-then-re-add, so it confirms once, at the remove; the re-add
 * is a new grant. New grants and Add-On removals (first purchase only, never a renewal) write
 * straight through, and so does everything when the count is unknown (`activeMemberships` absent) —
 * the editor is never blocked. The same count shows a standing notice over the recurring section.
 */
import { useEffect, useId, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { IconAction } from '@/components/IconAction';
import { Icon } from '@/theme';
import { useCreditBucketPickList } from '@/api/creditBuckets';
import { PickListLoader } from '@/components/PickListLoader';
import { formatCredits } from '@/utils/v2Format';
import { serverMessage } from '@/api/serverMessage';
import type { ShowToast } from '@/components/toastContext';
import type { PlanCredit, PlanCreditWrite } from '@/api/membershipTierCredits';

export interface PlanCreditGrantsEditorProps {
  /** The owning plan id (edit-mode); add-mode passes a placeholder — the callbacks stage. */
  planId: string;
  /** Load the plan's current grants (a stable reference). */
  loadCredits: (planId: string) => Promise<PlanCredit[]>;
  /** Upsert one grant on the plan (a stable reference). */
  setCredit: (planId: string, creditBucketId: string, input: PlanCreditWrite) => Promise<void>;
  /** Remove a grant from the plan (a stable reference). */
  deleteCredit: (planId: string, creditBucketId: string) => Promise<void>;
  showToast: ShowToast;
  /**
   * Members on the plan now (DNKAPI-0562 `impact.activeMemberships`). `> 0` shows the standing
   * credits notice and confirms a recurring-grant removal; absent/`null`/`0` does neither.
   */
  activeMemberships?: number | null;
}

const CHOOSE_BUCKET_ERROR = 'Choose a credit bucket.';
/** The plan-credit contract requires `amount > 0` (unlike the shared editor's `>= 0`). */
const AMOUNT_ERROR = 'Enter an amount greater than 0.';
const INT_ERROR = 'Enter a whole number of months/days, 1 or more.';
const SAVE_ERROR = 'Couldn’t save the credit grant.';
const REMOVE_ERROR = 'Couldn’t remove the credit grant.';
const LOAD_ERROR = 'Couldn’t load the credit grants.';
const EMPTY_BUCKETS_INFO = 'Create a credit bucket first to set this plan’s credit grants.';
const ALL_USED_HINT = 'Every credit bucket already has a grant on this plan.';

/** Parse a credit-amount text field to a finite number `> 0`, or null when invalid/blank. */
function parseAmount(text: string): number | null {
  const trimmed = text.trim();
  if (trimmed === '') return null;
  const n = Number(trimmed);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** Parse an add-on months/days field to a positive integer (≥ 1), or null when invalid. */
function parsePositiveInt(text: string): number | null {
  const trimmed = text.trim();
  if (trimmed === '') return null;
  const n = Number(trimmed);
  return Number.isFinite(n) && Number.isInteger(n) && n >= 1 ? n : null;
}

export function PlanCreditGrantsEditor({
  planId,
  loadCredits,
  setCredit,
  deleteCredit,
  showToast,
  activeMemberships = null,
}: PlanCreditGrantsEditorProps) {
  const ids = useId();
  const buckets = useCreditBucketPickList();

  const [rows, setRows] = useState<PlanCredit[]>([]);
  const [loadError, setLoadError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [busy, setBusy] = useState(false);
  // The recurring grant whose removal awaits the reduce-credits confirm (DNKADM-0407), or null.
  const [pendingRemove, setPendingRemove] = useState<string | null>(null);
  const members = activeMemberships !== null && activeMemberships > 0 ? activeMemberships : null;

  // Recurring add-row.
  const [recBucketId, setRecBucketId] = useState('');
  const [recAmount, setRecAmount] = useState('');
  const [recError, setRecError] = useState<string | null>(null);

  // Add-On add-row.
  const [addonBucketId, setAddonBucketId] = useState('');
  const [addonAmount, setAddonAmount] = useState('');
  const [reEligMode, setReEligMode] = useState<'once' | 'after'>('once');
  const [reEligMonths, setReEligMonths] = useState('');
  const [expiryMode, setExpiryMode] = useState<'never' | 'after'>('never');
  const [expiryDays, setExpiryDays] = useState('');
  const [addonError, setAddonError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const loaded = await loadCredits(planId);
        if (cancelled) return;
        setRows(loaded);
        setLoadError(false);
      } catch {
        if (cancelled) return;
        setLoadError(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [planId, reloadKey, loadCredits]);

  const bucketItems = buckets.items;
  const bucketOptions = useMemo(() => bucketItems ?? [], [bucketItems]);
  const nameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const b of bucketOptions) map.set(b.id, b.name || b.id);
    return map;
  }, [bucketOptions]);

  const recurringRows = useMemo(() => rows.filter((r) => r.recurrence === 'recurring'), [rows]);
  const addonRows = useMemo(() => rows.filter((r) => r.recurrence === 'add_on'), [rows]);

  // One grant per bucket ACROSS BOTH sections — a granted bucket is addable in neither.
  const granted = useMemo(() => new Set(rows.map((r) => r.creditBucketId)), [rows]);
  const addableBuckets = useMemo(
    () => bucketOptions.filter((b) => !granted.has(b.id)),
    [bucketOptions, granted],
  );
  const noneAddable = addableBuckets.length === 0;

  async function handleAddRecurring() {
    setRecError(null);
    if (recBucketId === '') {
      setRecError(CHOOSE_BUCKET_ERROR);
      return;
    }
    const amount = parseAmount(recAmount);
    if (amount === null) {
      setRecError(AMOUNT_ERROR);
      return;
    }
    setBusy(true);
    try {
      await setCredit(planId, recBucketId, { amount });
      setRecBucketId('');
      setRecAmount('');
      setReloadKey((k) => k + 1);
      showToast('Credit grant saved', 'success');
    } catch (err) {
      setRecError(serverMessage(err) ?? SAVE_ERROR);
    } finally {
      setBusy(false);
    }
  }

  async function handleAddAddon() {
    setAddonError(null);
    if (addonBucketId === '') {
      setAddonError(CHOOSE_BUCKET_ERROR);
      return;
    }
    const amount = parseAmount(addonAmount);
    if (amount === null) {
      setAddonError(AMOUNT_ERROR);
      return;
    }
    let reEligibilityLapseMonths = 0;
    if (reEligMode === 'after') {
      const n = parsePositiveInt(reEligMonths);
      if (n === null) {
        setAddonError(INT_ERROR);
        return;
      }
      reEligibilityLapseMonths = n;
    }
    let expiry = 0;
    if (expiryMode === 'after') {
      const n = parsePositiveInt(expiryDays);
      if (n === null) {
        setAddonError(INT_ERROR);
        return;
      }
      expiry = n;
    }
    setBusy(true);
    try {
      await setCredit(planId, addonBucketId, {
        amount,
        recurrence: 'add_on',
        reEligibilityLapseMonths,
        expiryDays: expiry,
      });
      setAddonBucketId('');
      setAddonAmount('');
      setReEligMode('once');
      setReEligMonths('');
      setExpiryMode('never');
      setExpiryDays('');
      setReloadKey((k) => k + 1);
      showToast('Credit grant saved', 'success');
    } catch (err) {
      setAddonError(serverMessage(err) ?? SAVE_ERROR);
    } finally {
      setBusy(false);
    }
  }

  async function handleRemove(creditBucketId: string) {
    setBusy(true);
    try {
      await deleteCredit(planId, creditBucketId);
      setReloadKey((k) => k + 1);
      showToast('Credit grant removed', 'success');
    } catch (err) {
      showToast(serverMessage(err) ?? REMOVE_ERROR, 'error');
    } finally {
      setBusy(false);
    }
  }

  // Removing a recurring grant cuts current members' next renewal: confirm first when anyone is on
  // the plan. Nothing is sent (or changed on screen) until the confirm.
  function requestRemoveRecurring(creditBucketId: string) {
    if (members === null) void handleRemove(creditBucketId);
    else setPendingRemove(creditBucketId);
  }

  const recBucketLabelId = `${ids}-rec-bucket-label`;
  const addonBucketLabelId = `${ids}-addon-bucket-label`;
  const reEligLabelId = `${ids}-reelig-label`;
  const expiryLabelId = `${ids}-expiry-label`;

  function bucketName(id: string): string {
    return nameById.get(id) ?? id;
  }

  return (
    <Box sx={{ mt: 1 }}>
      {loadError && (
        <Alert
          severity="error"
          sx={{ mb: 1, py: 0.5 }}
          action={
            <Button color="inherit" size="small" onClick={() => setReloadKey((k) => k + 1)}>
              Retry
            </Button>
          }
        >
          {LOAD_ERROR}
        </Alert>
      )}

      {/* (A) Recurring credits ------------------------------------------------ */}
      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
        Recurring credits
      </Typography>
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
        Granted every billing cycle, on purchase and each renewal.
      </Typography>
      {members !== null && (
        <Alert severity="info" role="status" sx={{ mb: 1, py: 0.5 }}>
          Changes here apply to current members at their next renewal. Their price does not change.
        </Alert>
      )}

      {recurringRows.length > 0 && (
        <Table size="small" aria-label="Recurring credits" sx={{ mb: 1 }}>
          <TableHead>
            <TableRow>
              <TableCell>Credit bucket</TableCell>
              <TableCell align="right">Amount</TableCell>
              <TableCell align="right">Remove</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {recurringRows.map((row) => (
              <TableRow key={row.creditBucketId}>
                <TableCell>{bucketName(row.creditBucketId)}</TableCell>
                <TableCell align="right">{formatCredits(row.amount)}</TableCell>
                <TableCell align="right">
                  <IconAction
                    size="small"
                    label={`Remove recurring credit for ${bucketName(row.creditBucketId)}`}
                    disabled={busy}
                    onClick={() => requestRemoveRecurring(row.creditBucketId)}
                  >
                    <Icon name="delete" />
                  </IconAction>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <PickListLoader list={buckets} label="credit buckets" loadingVariant="inline">
        {(options) =>
          options.length === 0 ? (
            <Alert severity="info" sx={{ py: 0.5 }}>
              {EMPTY_BUCKETS_INFO}
            </Alert>
          ) : (
            <>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1.5}
                sx={{ alignItems: 'flex-start' }}
              >
                <FormControl size="small" sx={{ minWidth: 200 }} disabled={busy || noneAddable}>
                  <InputLabel id={recBucketLabelId}>Credit bucket</InputLabel>
                  <Select
                    labelId={recBucketLabelId}
                    label="Credit bucket"
                    value={recBucketId}
                    onChange={(e) => setRecBucketId(e.target.value)}
                    slotProps={{ input: { 'aria-label': 'Recurring credit bucket' } }}
                  >
                    {addableBuckets.map((b) => (
                      <MenuItem key={b.id} value={b.id}>
                        {b.name || b.id}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  label="Amount"
                  type="number"
                  value={recAmount}
                  onChange={(e) => setRecAmount(e.target.value)}
                  disabled={busy || noneAddable}
                  size="small"
                  slotProps={{
                    htmlInput: {
                      'aria-label': 'Recurring amount',
                      min: 0,
                      step: '0.01',
                      inputMode: 'decimal',
                    },
                  }}
                />
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => void handleAddRecurring()}
                  disabled={busy || noneAddable}
                  sx={{ mt: 0.5 }}
                >
                  Add recurring credit
                </Button>
              </Stack>
              {noneAddable && (
                <Typography
                  variant="caption"
                  sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}
                >
                  {ALL_USED_HINT}
                </Typography>
              )}
            </>
          )
        }
      </PickListLoader>

      {recError && (
        <Alert severity="error" role="alert" sx={{ mt: 1, py: 0.5 }}>
          {recError}
        </Alert>
      )}

      {/* (B) Add-On credits (first purchase) --------------------------------- */}
      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5, mt: 3 }}>
        Add-On credits (first purchase)
      </Typography>
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
        Granted once on a qualifying first purchase — not on renewals. Use a bucket not shared with
        recurring credits (e.g. a “Welcome” bucket).
      </Typography>

      {addonRows.length > 0 && (
        <Table size="small" aria-label="Add-On credits" sx={{ mb: 1 }}>
          <TableHead>
            <TableRow>
              <TableCell>Credit bucket</TableCell>
              <TableCell align="right">Amount</TableCell>
              <TableCell>Re-eligibility</TableCell>
              <TableCell>Expiry</TableCell>
              <TableCell align="right">Remove</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {addonRows.map((row) => (
              <TableRow key={row.creditBucketId}>
                <TableCell>{bucketName(row.creditBucketId)}</TableCell>
                <TableCell align="right">{formatCredits(row.amount)}</TableCell>
                <TableCell>
                  {row.reEligibilityLapseMonths === 0
                    ? 'Grant once'
                    : `Re-eligible after ${row.reEligibilityLapseMonths} months`}
                </TableCell>
                <TableCell>
                  {row.expiryDays === 0
                    ? 'Never expires'
                    : `Expires ${row.expiryDays} days after purchase`}
                </TableCell>
                <TableCell align="right">
                  <IconAction
                    size="small"
                    label={`Remove Add-On credit for ${bucketName(row.creditBucketId)}`}
                    disabled={busy}
                    onClick={() => void handleRemove(row.creditBucketId)}
                  >
                    <Icon name="delete" />
                  </IconAction>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <PickListLoader list={buckets} label="credit buckets" loadingVariant="inline">
        {(options) =>
          options.length === 0 ? (
            <Alert severity="info" sx={{ py: 0.5 }}>
              {EMPTY_BUCKETS_INFO}
            </Alert>
          ) : (
            <>
              <Box
                sx={{
                  display: 'grid',
                  gap: 1.5,
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
                  alignItems: 'start',
                }}
              >
                <FormControl size="small" fullWidth disabled={busy || noneAddable}>
                  <InputLabel id={addonBucketLabelId}>Credit bucket</InputLabel>
                  <Select
                    labelId={addonBucketLabelId}
                    label="Credit bucket"
                    value={addonBucketId}
                    onChange={(e) => setAddonBucketId(e.target.value)}
                    slotProps={{ input: { 'aria-label': 'Add-On credit bucket' } }}
                  >
                    {addableBuckets.map((b) => (
                      <MenuItem key={b.id} value={b.id}>
                        {b.name || b.id}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  fullWidth
                  label="Amount"
                  type="number"
                  value={addonAmount}
                  onChange={(e) => setAddonAmount(e.target.value)}
                  disabled={busy || noneAddable}
                  size="small"
                  slotProps={{
                    htmlInput: {
                      'aria-label': 'Add-On amount',
                      min: 0,
                      step: '0.01',
                      inputMode: 'decimal',
                    },
                  }}
                />
                <FormControl size="small" fullWidth disabled={busy || noneAddable}>
                  <InputLabel id={reEligLabelId}>Re-eligibility</InputLabel>
                  <Select
                    labelId={reEligLabelId}
                    label="Re-eligibility"
                    value={reEligMode}
                    onChange={(e) => setReEligMode(e.target.value as 'once' | 'after')}
                    slotProps={{ input: { 'aria-label': 'Re-eligibility' } }}
                  >
                    <MenuItem value="once">Grant once</MenuItem>
                    <MenuItem value="after">Re-eligible after N months</MenuItem>
                  </Select>
                </FormControl>
                {reEligMode === 'after' && (
                  <TextField
                    fullWidth
                    label="Months"
                    type="number"
                    value={reEligMonths}
                    onChange={(e) => setReEligMonths(e.target.value)}
                    disabled={busy || noneAddable}
                    size="small"
                    slotProps={{
                      htmlInput: {
                        'aria-label': 'Months until re-eligible',
                        min: 1,
                        step: 1,
                        inputMode: 'numeric',
                      },
                    }}
                  />
                )}
                <FormControl size="small" fullWidth disabled={busy || noneAddable}>
                  <InputLabel id={expiryLabelId}>Expiry</InputLabel>
                  <Select
                    labelId={expiryLabelId}
                    label="Expiry"
                    value={expiryMode}
                    onChange={(e) => setExpiryMode(e.target.value as 'never' | 'after')}
                    slotProps={{ input: { 'aria-label': 'Expiry' } }}
                  >
                    <MenuItem value="never">Never expires</MenuItem>
                    <MenuItem value="after">Expires N days after purchase</MenuItem>
                  </Select>
                </FormControl>
                {expiryMode === 'after' && (
                  <TextField
                    fullWidth
                    label="Days"
                    type="number"
                    value={expiryDays}
                    onChange={(e) => setExpiryDays(e.target.value)}
                    disabled={busy || noneAddable}
                    size="small"
                    slotProps={{
                      htmlInput: {
                        'aria-label': 'Days until expiry',
                        min: 1,
                        step: 1,
                        inputMode: 'numeric',
                      },
                    }}
                  />
                )}
              </Box>
              <Button
                variant="outlined"
                size="small"
                onClick={() => void handleAddAddon()}
                disabled={busy || noneAddable}
                sx={{ mt: 1.5 }}
              >
                Add Add-On credit
              </Button>
              {noneAddable && (
                <Typography
                  variant="caption"
                  sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}
                >
                  {ALL_USED_HINT}
                </Typography>
              )}
            </>
          )
        }
      </PickListLoader>

      {addonError && (
        <Alert severity="error" role="alert" sx={{ mt: 1, py: 0.5 }}>
          {addonError}
        </Alert>
      )}

      {pendingRemove !== null && members !== null && (
        <ReduceCreditsConfirmDialog
          members={members}
          onCancel={() => setPendingRemove(null)}
          onConfirm={() => {
            const bucketId = pendingRemove;
            setPendingRemove(null);
            void handleRemove(bucketId);
          }}
        />
      )}
    </Box>
  );
}

/** Confirm a cut to current members' credits (DNKADM-0407) — the only gated grant write. */
function ReduceCreditsConfirmDialog({
  members,
  onCancel,
  onConfirm,
}: {
  members: number;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const titleId = `${useId()}-title`;
  return (
    <Dialog open onClose={onCancel} aria-labelledby={titleId} maxWidth="xs" fullWidth>
      <DialogTitle id={titleId}>
        {`Reduce credits for ${members} current ${members === 1 ? 'member' : 'members'}?`}
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ fontSize: 14 }}>
          They keep their current price. Their next renewal grants the new amounts.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} color="inherit">
          Cancel
        </Button>
        <Button onClick={onConfirm} color="error" variant="contained">
          Reduce credits
        </Button>
      </DialogActions>
    </Dialog>
  );
}
